/**
 * Laying out a master set's slot grid.
 *
 * A master set is a set crossed with ALL its printings, and a sub set is a
 * FILTER over those same slots rather than a set of its own. So a slot is a
 * (card, printing) pair: printings multiply and card ranges add, with no
 * special-casing anywhere. Base Set is 102 cards x 4 printings = 408 slots and
 * its 1st Edition sub set is the 102 of those whose printing is 1st Ed.
 *
 * WHAT THIS FILE IS, AND WHAT IT STOPPED BEING.
 *
 * Phase 4 had to mirror the ownership SQL in here, because phase 1 exposed
 * ownership only as a count and there was no per-slot read. `printingInScope`,
 * `cardInScope`, `fillSlots`, `countScopedSlots` and a `reconcile()` tripwire
 * all lived here and warned when they drifted from their SQL originals.
 *
 * They are gone. `wish_set_slot_grid` returns the slots already scoped with an
 * `owned` flag, and `wish_set_progress` counts those same rows, so there is one
 * implementation of the rule and nothing left to reconcile. What remains here is
 * pure layout: joining resolved slots to the catalogue's own card order, naming
 * and colouring the printing badge, and dropping what a filter excluded.
 */

import { slotPrintingKey, printingLabel, printingColor, printingTextColor, printingShortLabel } from './printings';

/** Every printing the rows hold, in the order badges should read down a grid. */
export function printingsOf(slots) {
  const seen = new Map();
  for (const s of slots || []) {
    const key = slotPrintingKey(s);
    if (!seen.has(key)) {
      seen.set(key, {
        key,
        subtype: s.printing_subtype ?? null,
        stamps: [...(s.printing_stamps || [])].filter(Boolean).sort(),
        foil: s.printing_foil ?? null,
        label: printingLabel(s.printing_subtype, s.printing_stamps, s.printing_foil),
      });
    }
  }
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key));
}

/** A slot's stable id: the card and the printing, which is what a slot IS. */
export function slotId(slot) {
  return `${slot.card_id}::${slotPrintingKey(slot)}`;
}

/**
 * The tiles to draw, in the order the catalogue handed the cards over.
 *
 * `slots` is what `wish_set_slot_grid` returned: already filtered to the scope,
 * already carrying `owned` and `printing_unknown`. Nothing here recomputes
 * either, which is the whole point of the phase 5 change.
 *
 * `cards` is the catalogue's answer to the current sort and filters - so the
 * order and the filtering are the database's, the same way they are in Explore
 * and in the picker, and a card the filters excluded has no slots here at all.
 * Cards with no slot rows are dropped rather than drawn empty: a slot that the
 * count does not count must never appear on screen.
 *
 * @param {{
 *   slots: object[], cards: object[], ownership?: boolean,
 *   missingOnly?: boolean, badges?: boolean,
 * }} input  ownership false draws every tile bright, which is what somebody
 *   else's list gets: their progress is not ours to fade.
 */
export function buildSlotTiles({
  slots = [], cards = [], ownership = true, missingOnly = false, badges = true,
}) {
  const byCard = new Map();
  for (const slot of slots) {
    if (!byCard.has(slot.card_id)) byCard.set(slot.card_id, []);
    byCard.get(slot.card_id).push(slot);
  }

  const tiles = [];
  for (const card of cards) {
    const own = byCard.get(card.id);
    if (!own) continue;
    for (const slot of inCardSlotOrder(own)) {
      const isOwned = ownership ? slot.owned === true : false;
      if (missingOnly && isOwned) continue;
      tiles.push({
        id: slotId(slot),
        cardId: slot.card_id,
        localId: slot.local_id,
        name: card.name || slot.card_name,
        setId: card.set_id,
        setName: card.set_name,
        imageUrl: card.image_url || null,
        // The price of THIS printing, which the slot row now carries. It used
        // to be the card's single catalogue price, so five Base Set Charizards
        // all read $489.47 while card_prices held $3,092 for the shadowless one
        // and $335 for the plain holo. Falls back to the card figure when a
        // printing has no price of its own, and says which it got, because
        // "this printing sells for X" and "this card sells for about X" are
        // different claims.
        price: slot.near_mint ?? card.near_mint ?? null,
        priceIsForPrinting: slot.price_is_for_printing === true,
        owned: isOwned,
        // Bright because the person owns the card, not because they own THIS
        // printing. Said on the tile, because a bar that counts it and a tile
        // that claims it are two different promises.
        printingUnknown: isOwned && slot.printing_unknown === true,
        badge: badges ? {
          label: printingShortLabel(slot.printing_subtype, slot.printing_stamps),
          full: printingLabel(slot.printing_subtype, slot.printing_stamps),
          color: printingColor(slot.printing_subtype, slot.printing_stamps),
          textColor: printingTextColor(slot.printing_subtype, slot.printing_stamps),
        } : null,
      });
    }
  }
  return tiles;
}

function inCardSlotOrder(slots) {
  return [...slots].sort((a, b) => {
    const la = printingLabel(a.printing_subtype, a.printing_stamps);
    const lb = printingLabel(b.printing_subtype, b.printing_stamps);
    return la.localeCompare(lb);
  });
}
